import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { describeJobSchedules } from "./manage-job";

type ManageJobDialogProps = {
  visible: boolean;
  job: any;
  isLoading: boolean;
  canDelete: boolean;
  deleting: boolean;
  onDeleteSchedule: (scheduleId: number) => void;
  onDeleteJob: () => void;
  onHide: () => void;
};

const formatDateTime = (value?: string | Date) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatEmployees = (row: any) => {
  const people = [...(row.supervisor ?? []), ...(row.employees ?? [])];
  if (!people.length) return "-";
  return people
    .map((person: any) => `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim())
    .filter(Boolean)
    .join(", ");
};

export function ManageJobDialog({
  visible,
  job,
  isLoading,
  canDelete,
  deleting,
  onDeleteSchedule,
  onDeleteJob,
  onHide,
}: ManageJobDialogProps) {
  const showLoading = isLoading || !job;
  const summary = !showLoading ? describeJobSchedules(job) : null;

  // The delete action and its explanation live in the footer, not the content
  // area: PrimeReact shrinks and scrolls .p-dialog-content when the dialog hits
  // its height cap, which silently cut the note off on shorter windows.
  const footer =
    showLoading || !canDelete ? null : (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "0.75rem",
          textAlign: "left",
        }}
      >
        <Button
          type="button"
          label="Delete entire job"
          icon="pi pi-trash"
          className="p-button-outlined p-button-danger"
          disabled={deleting}
          loading={deleting}
          onClick={onDeleteJob}
        />
        <small style={{ color: "#6b7280", lineHeight: 1.4 }}>
          Deleting the job frees this contract so a new job can be scheduled. Removing the last
          remaining schedule also deletes the job.
        </small>
      </div>
    );

  return (
    <Dialog
      visible={visible}
      header="Manage job & schedules"
      draggable={false}
      onHide={deleting ? () => undefined : onHide}
      closable={!deleting}
      closeOnEscape={!deleting}
      style={{ width: "40rem", maxWidth: "calc(100vw - 2rem)" }}
      contentStyle={{ padding: "1.25rem 1.5rem 1.5rem" }}
      footer={footer}
    >
      {showLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <ProgressSpinner style={{ width: "3rem", height: "3rem" }} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <div style={{ fontWeight: 600 }}>{job.name}</div>
            <div
              style={{
                marginTop: "0.35rem",
                color: summary?.isOrphan ? "#b45309" : undefined,
              }}
            >
              {summary?.message}
            </div>
          </div>

          {!summary?.isOrphan && (
            <DataTable value={job.jobSchedules ?? []} size="small" scrollable scrollHeight="16rem">
              <Column field="startTime" header="Start" body={(row) => formatDateTime(row.startTime)} />
              <Column field="endTime" header="End" body={(row) => formatDateTime(row.endTime)} />
              <Column header="Assigned employees" body={(row) => formatEmployees(row)} />
              {canDelete && (
                <Column
                  header=""
                  body={(row) => (
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-text p-button-danger"
                      disabled={deleting}
                      loading={deleting}
                      onClick={() => onDeleteSchedule(row.id)}
                    />
                  )}
                  style={{ width: "3rem" }}
                />
              )}
            </DataTable>
          )}

        </div>
      )}
    </Dialog>
  );
}
